import { fromUnixMilliseconds } from 'shared-components';
import { Temporal } from '@js-temporal/polyfill';
import { Status } from '../components/atoms/finger-doc/types';
import { EventBookingStatus, EventSystemStatus, IRecordedInterviewEntry, IScheduledEvent, RecordedInterviewSystemStatus } from 'api-paths-cas';
import { UNI_ROLES, UserRole } from '../store/auth/types';
import { FACIAL_RECOGNITION_SCORE_SCALE } from '../utilities/constants';

export function getFingerStatus(event: IScheduledEvent): Status {
  return event.systemStatus === EventSystemStatus.PASSED
    ? Status.Passed
    : event.systemStatus === EventSystemStatus.FAILED
      ? Status.Failed
      : event.systemStatus === EventSystemStatus.RESIT
        ? Status.Resit
        : event.bookingStatus === EventBookingStatus.BOOKED
          ? Status.Booked
          : event.bookingStatus === EventBookingStatus.CANCELED
            ? Status.Cancelled
            : Status.Requested;
}

export function getRecordedInterviewFingerStatus(
  interview: IRecordedInterviewEntry,
  isAttemptRecorded: boolean,
  isAgentOrAgencyAdmin: boolean,
): Status {
  let status: Status;
  if (isAttemptRecorded) {
    switch (interview.systemStatus) {
      case RecordedInterviewSystemStatus.PASSED:
        status = Status.Passed;
        break;
      case RecordedInterviewSystemStatus.FAILED:
        status = isAgentOrAgencyAdmin ? Status.Passed : Status.Failed;
        break;
      case RecordedInterviewSystemStatus.RESIT:
        status = isAgentOrAgencyAdmin ? Status.Passed : Status.Resit;
        break;
      default:
      case RecordedInterviewSystemStatus.RECORDED:
        status = Status.Recorded;
        break;
    }
  } else {
    switch (interview.systemStatus) {
      case RecordedInterviewSystemStatus.INVITED:
        status = Status.Invited;
        break;
      default:
      case RecordedInterviewSystemStatus.ATTEMPTED:
        status = Status.Attempted;
        break;
    }
  }
  return status;
}

export const getFormattedDateRange = (startDateMs?: number, endDateMs?: number): string => {
  if (!startDateMs || !endDateMs) {
    return '';
  }

  // Use fromUnixMilliseconds to create Temporal.Instant from milliseconds
  const startInstant = fromUnixMilliseconds(startDateMs);
  const endInstant = fromUnixMilliseconds(endDateMs);

  if (!startInstant || !endInstant) {
    return '';
  }

  // Convert to ZonedDateTime to get timezone info
  const startZoned = startInstant.toZonedDateTimeISO(Temporal.Now.timeZoneId());
  const endZoned = endInstant.toZonedDateTimeISO(Temporal.Now.timeZoneId());

  // Format the dates
  const startDateTime = startZoned.toPlainDateTime();
  const endDateTime = endZoned.toPlainDateTime();

  const format1 = startDateTime.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const format2 = endDateTime.toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Get timezone offset
  const offsetNs = startZoned.offsetNanoseconds;
  const offsetMinutes = offsetNs / 60000000000;
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const formatTz = `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

  return `${format1}-${format2} GMT${formatTz}`;
};

export const getFormattedDate = (date: number): string => {
  // Use fromUnixMilliseconds to create Temporal.Instant from milliseconds
  const instant = fromUnixMilliseconds(date);

  if (!instant) {
    return '';
  }

  // Convert to ZonedDateTime to get timezone info
  const zoned = instant.toZonedDateTimeISO(Temporal.Now.timeZoneId());
  const dateTime = zoned.toPlainDateTime();

  // Format the date
  const format = dateTime.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Get timezone offset
  const offsetNs = zoned.offsetNanoseconds;
  const offsetMinutes = offsetNs / 60000000000;
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const formatTz = `${sign}${String(hours).padStart(2, '0')}`;

  return `${format} GMT${formatTz}`;
};

export const getInterviewAccess = (data: {
  userRole: UserRole;
  hasRoleAccess: boolean;
  isPathwayStudent: boolean;
  isPathwayProvider: boolean;
  isAddAndCancelInterviewVisible: boolean;
}): boolean => {
  const { userRole, hasRoleAccess, isPathwayStudent, isPathwayProvider, isAddAndCancelInterviewVisible } = data;
  if (isPathwayProvider) {
    return hasRoleAccess;
  }
  if (isPathwayStudent && UNI_ROLES.includes(userRole)) {
    return hasRoleAccess && isAddAndCancelInterviewVisible;
  }
  return hasRoleAccess;
};

export const getFacialRecognitionScore = (initialScore: number | undefined | null): number | string => {
  // Return 0 for null/undefined values instead of 'NA'
  if (initialScore === null || initialScore === undefined) return 0;
  // Only return 'NA' for truly invalid non-number values
  if (typeof initialScore !== 'number') return 'NA';
  const score = Number((initialScore / FACIAL_RECOGNITION_SCORE_SCALE).toPrecision(2));

  if (score >= 10) return 9.9;

  return score;
};
